/**
 * Receipt uploader. Drag-drop receipt images + an edit-details modal that
 * appends line items to the expense report.
 *
 * NOTE: the backend endpoint (POST /api/my/expenses/{reportId}/receipts)
 * still runs Document Intelligence OCR and returns extracted fields. We
 * currently ignore those fields and show a fully-manual form to the user —
 * AI extraction UX is deferred until we're ready to advertise it. When that
 * day comes: re-populate `form` from `result.extracted` inside
 * handleFilesSelected and restore the AI status banner inside the modal.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, Loader2, Check, X, ChevronRight, ImageIcon, FileText, Save } from 'lucide-react';

// Receipt-scanner accepts both photo formats and PDFs. The Document
// Intelligence prebuilt-receipt model handles both, so we just need to
// keep PDFs rendering correctly in the preview (icon thumbnail + iframe
// for the large view) instead of using <img>.
const ACCEPT_TYPES = 'image/*,application/pdf';
function isPdf(file) {
  return (
    (file?.type || '').toLowerCase() === 'application/pdf' ||
    /\.pdf$/i.test(file?.name || '')
  );
}
function isAcceptedReceipt(file) {
  const t = (file?.type || '').toLowerCase();
  return t.startsWith('image/') || t === 'application/pdf' || /\.pdf$/i.test(file?.name || '');
}
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import CurrencyCombobox from '@/components/expense/CurrencyCombobox';
import { useFxRate } from '@/hooks/useFxRate.jsx';
import { FOREIGN_TXN_FEE_RATE } from '@/lib/currencies.js';
import Badge from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { expenseApi } from '@/lib/api';

// Currency list now lives in src/lib/currencies.js, surfaced via CurrencyCombobox.

// Hard cap enforced client-side for UX (immediate feedback, no round trip)
// and server-side in my-expenses-upload-receipt (the authoritative guard).
const MAX_RECEIPTS = 10;
const LIMIT_MESSAGE = 'Each claim is limited to 10 receipts — please submit additional expenses under a separate claim.';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Mirrors the backend calc in api/my-expenses-line-items: baseSar is the
// plain conversion (still keyed on the currency being non-SAR — SAR is
// always the reporting/base currency), and a line in a currency other than
// the employee's home currency adds a 3% foreign transaction fee on top of
// baseSar. sarAmount is the all-in total (base + fee). Takes homeCurrency
// as a parameter (rather than being module-scope keyed on 'SAR') so both
// the modal's live display and the add/close handlers below use identical
// math for whichever employee is filling out the report.
function computeSarBreakdown(form, homeCurrency) {
  const amt = parseFloat(form.currencyAmount || 0) || 0;
  const rate = parseFloat(form.exchangeRate || 1) || 1;
  const isNonSarCurrency = (form.currencyType || 'SAR') !== 'SAR';
  const baseSar = round2(amt * (isNonSarCurrency ? rate : 1));
  const isForeign = (form.currencyType || 'SAR').toUpperCase() !== (homeCurrency || 'SAR').toUpperCase();
  const foreignFeeSar = isForeign ? round2(baseSar * FOREIGN_TXN_FEE_RATE) : 0;
  const sarAmount = round2(baseSar + foreignFeeSar);
  return { baseSar, foreignFeeSar, sarAmount };
}

const EMPTY_FORM = {
  date: '',
  expenseTypeId: '',
  description: '',
  currencyType: 'SAR',
  currencyAmount: '',
  exchangeRate: '1',
  vendorName: '',
  chargeAccount: '',
  projectPermitted: false,
  // true = paid on the company card (finance already paid; don't
  // reimburse to the employee's personal account). The approval
  // email surfaces the company-card total so finance sees the
  // breakdown at a glance.
  companyCardUsed: false,
};

export default function ReceiptScanner({ reportId, ensureReport, expenseTypes, homeCurrency, onLineItemAdded, onReceiptRemoved, onReceiptsChange, initialReceipts }) {
  const [receipts, setReceipts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [limitNotice, setLimitNotice] = useState(false);
  const fileRef = useRef(null);
  const seededRef = useRef(false);

  // Edit mode: seed the list with receipts already on the report (passed
  // in pre-merged with their line-item data by the parent) so the user
  // can see each uploaded receipt and re-edit its details. Seed once.
  useEffect(() => {
    if (seededRef.current) return;
    if (!Array.isArray(initialReceipts) || initialReceipts.length === 0) return;
    seededRef.current = true;
    setReceipts(initialReceipts.map((r) => ({
      id: r.id,
      previewUrl: r.previewUrl,
      isPdf: !!r.isPdf,
      fileName: r.fileName || '',
      status: 'ready',
      existing: true,
      receiptRowKey: r.receiptRowKey || null,
      form: { ...EMPTY_FORM, ...(r.form || {}) },
    })));
  }, [initialReceipts]);

  const selected = receipts.find((r) => r.id === selectedId) || null;

  useEffect(() => {
    onReceiptsChange?.(receipts.length);
  }, [receipts.length, onReceiptsChange]);

  // handleFilesSelected is memoized (deps: reportId, ensureReport only) and
  // reads/writes `receipts` via the functional setState form to avoid stale
  // closures, so it can't reference `receipts.length` directly for the cap
  // check. Mirror the count in a ref instead — always current, no re-memo.
  const receiptCountRef = useRef(0);
  useEffect(() => {
    receiptCountRef.current = receipts.length;
  }, [receipts.length]);

  // Auto-persist partial details when the modal is closed via X, Escape,
  // or backdrop click — not just the explicit "Save & Add to Report"
  // button. Without this, details typed in but not explicitly saved were
  // silently discarded. Upserts by itemKey so re-opening/closing repeatedly
  // never creates duplicate rows.
  const closeModal = () => {
    if (selected && selected.status !== 'added') {
      const f = selected.form;
      const hasData = !!(f.date || f.currencyAmount || f.expenseTypeId || f.description || f.vendorName);
      if (hasData) {
        const { baseSar, foreignFeeSar, sarAmount } = computeSarBreakdown(f, homeCurrency);
        onLineItemAdded({
          itemKey: selected.id,
          date: f.date,
          expenseTypeId: f.expenseTypeId,
          description: f.description,
          currencyType: f.currencyType,
          currencyAmount: f.currencyAmount ? parseFloat(f.currencyAmount) : '',
          exchangeRate: parseFloat(f.exchangeRate || 1),
          baseSar,
          foreignFeeSar,
          sarAmount,
          chargeAccount: f.chargeAccount,
          projectPermitted: f.projectPermitted,
          vendorName: f.vendorName,
          companyCardUsed: f.companyCardUsed === true,
        });
      }
    }
    setSelectedId(null);
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!selected) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && closeModal();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Multi-file upload ──
  const handleFilesSelected = useCallback(async (files) => {
    if (!files.length) return;

    // The parent passes a stub reportId ("draft") until the user has
    // saved their claim purpose and we have a real report row. If the
    // parent provided an ensureReport callback, ask it to materialize
    // the draft first — otherwise the upload would 404 with "Expense
    // report not found".
    let activeReportId = reportId;
    if (!activeReportId || activeReportId === 'draft') {
      if (typeof ensureReport === 'function') {
        try {
          activeReportId = await ensureReport();
        } catch (err) {
          console.error('[receipt-upload] ensureReport failed', err);
        }
      }
      if (!activeReportId || activeReportId === 'draft') {
        // Couldn't materialize — surface a one-off entry so the user
        // sees why their drag-and-drop didn't take.
        const blocked = Array.from(files).filter(isAcceptedReceipt).map((f) => ({
          id: crypto.randomUUID(),
          file: f,
          previewUrl: URL.createObjectURL(f),
          isPdf: isPdf(f),
          status: 'failed',
          error: 'Save the report first (claim purpose required) so we can attach receipts to it.',
          form: { ...EMPTY_FORM },
        }));
        if (blocked.length > 0) setReceipts((prev) => [...prev, ...blocked]);
        return;
      }
    }

    let accepted = Array.from(files).filter(isAcceptedReceipt);

    // Hard cap: only take as many files as there are slots left. The
    // backend re-checks this (authoritative); this is just fast feedback
    // so the employee isn't surprised by a failed upload after the fact.
    const remainingSlots = MAX_RECEIPTS - receiptCountRef.current;
    if (accepted.length > Math.max(remainingSlots, 0)) {
      setLimitNotice(true);
      accepted = accepted.slice(0, Math.max(remainingSlots, 0));
    } else {
      setLimitNotice(false);
    }

    const newEntries = accepted.map((f) => ({
      id: crypto.randomUUID(),
      file: f,
      previewUrl: URL.createObjectURL(f),
      isPdf: isPdf(f),
      status: 'uploading',
      form: { ...EMPTY_FORM },
    }));

    if (!newEntries.length) return;

    setReceipts((prev) => [...prev, ...newEntries]);
    setUploading(true);

    // Upload sequentially to avoid rate limits. The previous loop
    // swallowed every error so the user wasn't bothered by AI-scanner
    // failures, but it also hid genuine upload failures — the row was
    // marked 'ready' even when the blob never landed. Admins then saw
    // the report missing the receipt and the user assumed it was
    // attached. Track per-entry errors and reflect them in status so
    // failed uploads are visible to the employee.
    for (const entry of newEntries) {
      let errMessage = null;
      let receiptRowKey = null;
      try {
        const formData = new FormData();
        formData.append('receipt', entry.file);
        // entry.id is this receipt's stable client key. Stamp it on the
        // uploaded receipt AND (below, in handleAddToReport) on the line
        // item created from it, so the two stay linked through save.
        formData.append('itemKey', entry.id);
        const res = await expenseApi.uploadReceipt(activeReportId, formData);
        receiptRowKey = res?.receipt?.rowKey || null;
      } catch (err) {
        errMessage = err?.message || 'Upload failed';
        console.error('[receipt-upload] failed for', entry.file?.name, err);
      }
      setReceipts((prev) =>
        prev.map((r) =>
          r.id === entry.id
            ? { ...r, status: errMessage ? 'failed' : 'ready', error: errMessage, receiptRowKey }
            : r
        )
      );
    }

    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';

    // Auto-open the first uploaded receipt so the user lands directly in
    // the "fill in details" flow instead of staring at the table.
    if (newEntries[0]) setSelectedId(newEntries[0].id);
  }, [reportId, ensureReport]);

  const handleFileInput = (e) => {
    handleFilesSelected(e.target.files);
  };

  // ── Drag & drop ──
  const [dragging, setDragging] = useState(false);
  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFilesSelected(e.dataTransfer.files);
  };

  // ── Form updates for selected receipt ──
  const updateForm = (field, value) => {
    setReceipts((prev) =>
      prev.map((r) =>
        r.id === selectedId ? { ...r, form: { ...r.form, [field]: value } } : r
      )
    );
  };

  // Auto-fill the exchange rate (-> SAR) for the selected receipt's date.
  // Editable; an already-saved rate on an existing receipt is left alone.
  const fx = useFxRate({
    currency: selected?.form.currencyType,
    date: selected?.form.date,
    hasExistingRate: !!(selected?.existing && selected.form.currencyType !== 'SAR'
      && selected.form.exchangeRate && selected.form.exchangeRate !== '1'),
    onRate: (rate) => updateForm('exchangeRate', String(rate)),
  });

  // ── Add to report ──
  const handleAddToReport = () => {
    if (!selected) return;
    const f = selected.form;
    if (!f.date || !f.currencyAmount || !f.expenseTypeId) return;

    const { baseSar, foreignFeeSar, sarAmount } = computeSarBreakdown(f, homeCurrency);
    onLineItemAdded({
      // Same key the receipt was uploaded with — links item ↔ receipt.
      itemKey: selected.id,
      date: f.date,
      expenseTypeId: f.expenseTypeId,
      description: f.description,
      currencyType: f.currencyType,
      currencyAmount: parseFloat(f.currencyAmount),
      exchangeRate: parseFloat(f.exchangeRate || 1),
      baseSar,
      foreignFeeSar,
      sarAmount,
      chargeAccount: f.chargeAccount,
      projectPermitted: f.projectPermitted,
      vendorName: f.vendorName,
      companyCardUsed: f.companyCardUsed === true,
    });

    setReceipts((prev) => prev.map((r) => (r.id === selectedId ? { ...r, status: 'added' } : r)));
    setSelectedId(null);
  };

  const removeReceipt = (id) => {
    const r = receipts.find((x) => x.id === id);
    if (r?.previewUrl) URL.revokeObjectURL(r.previewUrl);
    setReceipts((prev) => prev.filter((x) => x.id !== id));
    if (selectedId === id) setSelectedId(null);
    // Best-effort backend delete of the uploaded blob. The real guarantee
    // that a removed receipt doesn't reappear is the submit-time prune
    // (backend removes receipts whose itemKey has no matching line item),
    // so we don't block the UI on this.
    if (r?.receiptRowKey && reportId && reportId !== 'draft') {
      expenseApi.deleteReceipt(reportId, r.receiptRowKey).catch(() => {});
    }
    // Tell the parent to drop the matching line item so it doesn't get
    // re-saved on the next save/submit.
    onReceiptRemoved?.(id);
  };

  const addedCount = receipts.filter((r) => r.status === 'added').length;
  const atReceiptLimit = receipts.length >= MAX_RECEIPTS;
  const sarBreakdown = selected ? computeSarBreakdown(selected.form, homeCurrency) : { baseSar: 0, foreignFeeSar: 0, sarAmount: 0 };
  const isForeignSelected = !!selected && (selected.form.currencyType || 'SAR').toUpperCase() !== (homeCurrency || 'SAR').toUpperCase();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-primary" />
          Receipts
        </CardTitle>
        <CardDescription>
          Upload receipt images, then fill in the details for each.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ── Drop zone ── */}
        {receipts.length === 0 ? (
          <label
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 cursor-pointer transition-all ${
              dragging
                ? 'border-primary bg-primary/5'
                : 'border-border/60 bg-muted/20 hover:border-primary/30 hover:bg-muted/40'
            }`}
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">
                Drop receipts here or click to browse
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Upload one or more receipts (image or PDF). You'll add the date, amount, currency and vendor for each.
              </p>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT_TYPES}
              multiple
              className="hidden"
              onChange={handleFileInput}
            />
          </label>
        ) : atReceiptLimit ? (
          <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 px-4 py-2.5 text-sm text-muted-foreground">
            Limit reached — each claim is limited to {MAX_RECEIPTS} receipts. Submit additional expenses under a separate claim.
          </div>
        ) : (
          <label className="flex items-center gap-2 rounded-lg border border-dashed border-border/60 px-4 py-2.5 cursor-pointer hover:border-primary/30 hover:bg-muted/30 transition-all">
            <Upload className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Add more receipts</span>
            {uploading && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary ml-auto" />}
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT_TYPES}
              multiple
              className="hidden"
              onChange={handleFileInput}
            />
          </label>
        )}

        {/* Inline notice when a selection was trimmed to fit the cap. */}
        {limitNotice && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            <span>{LIMIT_MESSAGE}</span>
          </div>
        )}

        {/* ── Receipt list ── */}
        {receipts.length > 0 && (
          <div>
            <div className="text-xs text-muted-foreground mb-2">
              {receipts.length} receipt{receipts.length !== 1 ? 's' : ''}
              {addedCount > 0 && ` · ${addedCount} added to report`}
            </div>
            <div className="divide-y divide-border rounded-lg border border-border overflow-hidden">
              {receipts.map((r) => (
                <div
                  key={r.id}
                  onClick={() => r.status !== 'uploading' && setSelectedId(r.id === selectedId ? null : r.id)}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                    selectedId === r.id
                      ? 'bg-primary/5 border-l-2 border-l-primary'
                      : r.status === 'ready'
                        ? 'bg-primary/5 hover:bg-primary/10 ring-1 ring-inset ring-primary/30'
                        : 'hover:bg-muted/50'
                  } ${r.status === 'added' ? 'opacity-60' : ''}`}
                >
                  {r.isPdf ? (
                    <div className="h-10 w-10 rounded-md bg-muted shrink-0 flex items-center justify-center text-muted-foreground">
                      <FileText className="h-5 w-5" />
                    </div>
                  ) : (
                    <img
                      src={r.previewUrl}
                      alt=""
                      className="h-10 w-10 rounded-md object-cover bg-muted shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate text-foreground">
                      {r.form.vendorName || 'Unknown vendor'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.form.date || 'No date'}
                      {r.form.currencyAmount
                        ? ` · ${r.form.currencyType} ${r.form.currencyAmount}`
                        : ''}
                    </div>
                  </div>

                  {r.status === 'uploading' && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                  )}
                  {r.status === 'ready' && !r.existing && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground shadow-sm animate-pulse">
                      Click to enter details
                    </span>
                  )}
                  {r.status === 'ready' && r.existing && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      Click to review
                    </span>
                  )}
                  {r.status === 'failed' && (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 border border-destructive/30 px-2.5 py-1 text-xs font-semibold text-destructive shadow-sm"
                      title={r.error || 'Upload failed'}
                    >
                      Upload failed — re-attach
                    </span>
                  )}
                  {r.status === 'added' && <Badge variant="info">Added</Badge>}

                  {r.status !== 'added' && r.status !== 'uploading' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeReceipt(r.id); }}
                      className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}

                  {r.status !== 'uploading' && (
                    <ChevronRight className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${selectedId === r.id ? 'rotate-90' : ''}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Full-screen modal for receipt detail ── */}
        {selected && selected.status !== 'uploading' && (
          <div
            className="fixed inset-0 z-50 flex items-stretch bg-foreground/20 backdrop-blur-[3px]"
            onClick={closeModal}
            style={{ animation: 'fadeIn 150ms ease-out' }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative flex w-full max-w-6xl mx-auto my-4 lg:my-6 rounded-2xl border border-border bg-card shadow-2xl overflow-hidden"
              style={{ animation: 'slideUp 200ms ease-out' }}
            >
              {/* Left: Form fields */}
              <div className="flex-1 min-w-0 overflow-y-auto p-6 lg:p-8 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Edit Receipt Details</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {selected.form.vendorName || 'Unknown vendor'}
                      {selected.form.currencyAmount ? ` · ${selected.form.currencyType} ${selected.form.currencyAmount}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={closeModal}
                    className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-all"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* AI status banners intentionally removed. See the file-level
                    JSDoc for how to restore them when AI extraction ships. */}

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Date"
                    type="date"
                    value={selected.form.date}
                    onChange={(e) => updateForm('date', e.target.value)}
                  />
                  <Select
                    label="Expense Type"
                    value={selected.form.expenseTypeId}
                    onChange={(e) => updateForm('expenseTypeId', e.target.value)}
                    options={[
                      { value: '', label: 'Select type...' },
                      ...expenseTypes.map((t) => ({ value: t.rowKey, label: t.displayName })),
                    ]}
                  />
                </div>

                <Input
                  label="Vendor / Merchant"
                  value={selected.form.vendorName}
                  onChange={(e) => updateForm('vendorName', e.target.value)}
                  placeholder="e.g. Uber, Marriott, Emirates"
                />

                <Input
                  label="Description & Reason (required)"
                  value={selected.form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  placeholder="Describe the expense purpose"
                />

                <div className="grid grid-cols-3 gap-3">
                  <CurrencyCombobox
                    value={selected.form.currencyType}
                    onChange={(code) => { updateForm('currencyType', code); if (code === 'SAR') updateForm('exchangeRate', '1'); }}
                  />
                  <Input
                    label="Amount"
                    type="number"
                    step="0.01"
                    value={selected.form.currencyAmount}
                    onChange={(e) => updateForm('currencyAmount', e.target.value)}
                    placeholder="0.00"
                  />
                  <Input
                    label={fx.loading ? 'Exchange Rate · fetching…' : fx.error ? 'Exchange Rate · enter manually' : 'Exchange Rate'}
                    type="number"
                    step="0.0001"
                    value={selected.form.exchangeRate}
                    onChange={(e) => updateForm('exchangeRate', e.target.value)}
                    placeholder="1"
                    disabled={selected.form.currencyType === 'SAR'}
                  />
                </div>

                {isForeignSelected ? (
                  <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 space-y-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Converted</span>
                      <span className="font-mono">{sarBreakdown.baseSar.toFixed(2)} SAR</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Foreign txn fee (3%)</span>
                      <span className="font-mono">{sarBreakdown.foreignFeeSar.toFixed(2)} SAR</span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-border/60">
                      <span className="text-muted-foreground font-medium">Total</span>
                      <span className="font-semibold text-profun-cyan">{sarBreakdown.sarAmount.toFixed(2)} SAR</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">SAR Amount:</span>
                    <span className="font-semibold text-profun-cyan">{sarBreakdown.sarAmount.toFixed(2)} SAR</span>
                  </div>
                )}

                <Input
                  label="Charge Account"
                  value={selected.form.chargeAccount}
                  onChange={(e) => updateForm('chargeAccount', e.target.value)}
                  placeholder="e.g. Transpo, Hotel, etc."
                />

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.form.projectPermitted}
                    onChange={(e) => updateForm('projectPermitted', e.target.checked)}
                  />
                  <span className="text-sm text-muted-foreground">Project contract permitted</span>
                </label>

                <label className="flex items-start gap-2 cursor-pointer rounded-md border border-border bg-card p-2.5">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={selected.form.companyCardUsed === true}
                    onChange={(e) => updateForm('companyCardUsed', e.target.checked)}
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground">Company Card Used</div>
                    <div className="text-[11px] text-muted-foreground">
                      Tick if this expense was paid on the company card. The approval email
                      flags it so finance doesn't reimburse the amount to your personal account.
                    </div>
                  </div>
                </label>

                {selected.status === 'added' ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/15 text-sm text-primary">
                      <Check className="h-4 w-4" />
                      Saved &amp; added to report
                    </div>
                    <Button variant="secondary" onClick={closeModal} className="w-full">
                      Close
                    </Button>
                  </div>
                ) : (
                  /* Explicit Save button (with icon + close-on-save) so the
                     employee gets clear confirmation that their edits were
                     captured. The button was previously labelled "Add to
                     Report" which felt like a one-shot action and made
                     people unsure if simply closing the dialog would lose
                     their changes. */
                  <Button
                    onClick={handleAddToReport}
                    disabled={!selected.form.date || !selected.form.currencyAmount || !selected.form.expenseTypeId}
                    className="w-full"
                  >
                    <Save className="h-4 w-4 mr-2" /> Save &amp; Add to Report
                  </Button>
                )}
              </div>

              {/* Right: Large receipt preview — takes up ~half the modal */}
              <div className="hidden lg:block w-[45%] shrink-0 bg-foreground/[0.03] border-l border-border">
                <div className="h-full overflow-auto p-4 flex items-start justify-center">
                  {selected.isPdf ? (
                    <iframe
                      src={selected.previewUrl}
                      title="Receipt PDF"
                      className="w-full h-full min-h-[480px] rounded-lg border border-border bg-card"
                    />
                  ) : (
                    <img
                      src={selected.previewUrl}
                      alt="Receipt"
                      className="w-full object-contain rounded-lg"
                    />
                  )}
                </div>
              </div>

              {/* Mobile: preview at bottom */}
              <div className="lg:hidden absolute bottom-0 left-0 right-0 border-t border-border bg-card p-3">
                {selected.isPdf ? (
                  <a
                    href={selected.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground"
                  >
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate flex-1">{selected.file?.name || 'PDF receipt'}</span>
                    <span className="text-xs text-primary">Open</span>
                  </a>
                ) : (
                  <img
                    src={selected.previewUrl}
                    alt="Receipt"
                    className="w-full max-h-40 object-contain rounded-lg"
                  />
                )}
              </div>
            </div>

            <style>{`
              @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
              @keyframes slideUp { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
            `}</style>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
